import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import CampaignDetailsCard from "./Components/CampaignDetailsCard";
import OperatorMessagePreviewCard from "./Components/OperatorMessagePreviewCard";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../State/store";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "../../Components/ui/toaster";
import { Button } from "../../Components/ui/button";
import { useToast } from "../../Components/ui/use-toast";
import RejectCampaignDialog from "./Components/RejectCampaignDialog";
import { setAccountId } from "@/src/State/slices/AuthenticationSlice";
import { revertCampaignWindowFromUTC } from "../../../src/Pages/Campaign/CreateCampaign/utils/revertCampaignWindowFromUTC";


// --- OperatorKey interface for preview dropdown ---
export interface OperatorKey {
  channel: string;
  country: string;
  operatorId: string | number;
  label: string;
}

const OperatorCampaignApprovalPage: React.FC = () => {
  const location = useLocation();
  // --- Static (or dynamic via props) campaign ID ---
  const campaignId = location.state?.campaignId;

  // --- Redux selectors for API base URLs ---
  const operatorUrl = useSelector(
    (state: RootState) => state.authentication.operatorUrl
  );
  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );
  const operatorId = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );
  const toast = useToast();
  // const operatorId = 1700;
  // --- Campaign core info ---
  const [campaignName, setCampaignName] = useState("");
  const [channels, setChannels] = useState<{ id: number; name: string }[]>([]);
  const [fromCountryName, setFromCountryName] = useState<string[]>([]);
  const [toCountryName, setToCountryName] = useState<string>("");
  const [countryNameMap, setCountryNameMap] = useState<Record<string, string>>(
    {}
  );
  const [translatedTemplates, setTranslatedTemplates] = useState<any[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [travellerCount, setTravellerCount] = useState("");
  const [premiumNumbers, setPremiumNumbers] = useState(false);
  const [duplicateMessages, setDuplicateMessages] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<string>("Pending");
  const [campaignApprovalStatus, setCampaignApprovalStatus] =
    useState<boolean>(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const navigate = useNavigate();

  // --- Tab/filter state for CampaignDetailsCard ---
  const [activeChannel, setActiveChannel] = useState<number>(1);
  const [activeCountry, setActiveCountry] = useState<string>("");
  const [activeConfigId, setActiveConfigId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState("");
    const [isDeleted, setIsDeleted] = useState(false);

  // --- Preview dropdown and template mapping ---
  const [operatorKeys, setOperatorKeys] = useState<OperatorKey[]>([]);
  const [selectedOperatorKey, setSelectedOperatorKey] =
    useState<OperatorKey | null>(null);
  const [operatorTemplates, setOperatorTemplates] = useState<
    Record<string, Record<string, Record<string, any>>>
  >({});

  const impersonatorAID = useSelector(
    (state: RootState) => state.admin.Impersonator?.ImpersonatorAID
  );

  const isImpersonated = useSelector(
    (state: RootState) => state.admin.Impersonator?.ImpersonationState
  );

  const accountId = useSelector(
    (state: RootState) => state.authentication.account_id
  );

  const workspaceId = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );

  //for N8N
  const [advertiserAccountId, setAdvertiserAccountId] = useState<number | null>(null);
  const [advertiserWorkspaceId, setAdvertiserWorkspaceId] = useState<
    number | null
  >(null);
  const [advertiserEmail, setAdvertiserEmail] = useState<string>("");
  const [operatorMappingId, setOperatorMappingId] = useState<string[]>([]);
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
    const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );
  const workspaceName = useSelector(
    (state: RootState) => state.authentication.workspaceName
  );



  // --- Loading state ---
  const [isLoading, setIsLoading] = useState(false);

  // --- API: fetch and preprocess campaign data ---
  useEffect(() => {
    if (!operatorId) return; // Don't fetch until operatorId is available!
    setIsLoading(true);
    
    (async () => {
      try {
        await handleCheckDeletedStatus();

        const response = await axios.get(`${operatorUrl}/CheckCampaignOperatorStatus`, {
          params: {
            campaignId: campaignId, // ensure campaignId is available in scope
            operatorId: operatorId
          },
        });

        const campaignStatus = response.data?.operatorStatus?.toLowerCase();

        setCampaignStatus(campaignStatus);

        const res = await axios.get(
          `${operatorUrl}/GetCampaignDetailsByIdForOperatorApproval`,
          { params: { campaignId, operatorId } }
        );
        if (res.data?.status === "Success" || res.data?.Status === "Success") {
          const data = res.data.data || res.data.Data;
          setCampaignName(data.campaign_name ?? "");
          setTranslatedTemplates(
            Array.isArray(data.translated_templates)
              ? data.translated_templates
              : []
          );

          //ForN8N
          setAdvertiserAccountId(data.user_account_id);
          setAdvertiserWorkspaceId(data.advertiser_workspace_id)
          setAdvertiserEmail(data.advertiser_email);
          setOperatorMappingId(data.operator_mapping_ids);

          // --- Channel list for tab UI ---
          const chTabs = Array.isArray(data.availablechannels)
            ? data.availablechannels.map((c: any) => ({
              id: c.channel_id,
              name: c.channel_name,
            }))
            : [];
          setChannels(chTabs);

          // --- Countries in templates (uppercased and deduped) ---
          const allCountries = (data.translated_templates || []).map((t: any) =>
            t.country_iso?.toUpperCase()
          );
          const uniqueCountries = allCountries.filter(
            (iso: string, idx: number, arr: string[]) =>
              iso && arr.indexOf(iso) === idx
          );
          setActiveChannel(chTabs[0]?.id || 1);
          setActiveCountry(uniqueCountries[0] || "");

          // --- Country names (API for human-readable) ---
          const fromIso = Array.isArray(data.from_country_iso)
            ? data.from_country_iso
            : [];
          const toIso =
            typeof data.to_country_iso === "string" ? data.to_country_iso : "";
          const isoCodes = []
            .concat(fromIso)
            .concat(toIso)
            .concat(uniqueCountries)
            .filter(Boolean)
            .map((iso: string) => iso.toLowerCase());
          const dedupedIsoCodes = isoCodes.filter(
            (iso: string, idx: number, arr: string[]) =>
              arr.indexOf(iso) === idx
          );
          if (dedupedIsoCodes.length) {
            try {
              const countryRes = await axios.post(
                `${adminUrl}/GetCountryListByIso`,
                {
                  IsoCodes: dedupedIsoCodes,
                }
              );
              if (
                countryRes.data.status === "Success" &&
                Array.isArray(countryRes.data.countries)
              ) {
                const isoMap: Record<string, string> = {};
                for (let c of countryRes.data.countries) {
                  isoMap[c.country_iso.toUpperCase()] = c.country_name;
                }
                setCountryNameMap(isoMap);
                setFromCountryName(
                  fromIso.map((iso: string) => isoMap[iso.toUpperCase()] || iso)
                );
                setToCountryName(isoMap[toIso.toUpperCase()] || toIso || "");
              }
            } catch {
              setFromCountryName(fromIso);
              setToCountryName(toIso);
              setCountryNameMap({});
            }
          }


          debugger

          const cleanedTimezone =
            data.selected_to_country_timezone?.split(" ")?.[0] || "UTC";


          debugger

          const onlyDate = (datetimeStr: string) => datetimeStr.split("T")[0];

          const {
            localStartDate,
            localStartTime,
            localEndDate,
            localEndTime,
          } = revertCampaignWindowFromUTC(
            onlyDate(data.start_date),
            data.start_time,
            onlyDate(data.end_date),
            data.end_time,
            cleanedTimezone
          );

          setStartDate(localStartDate ?? "");
          setEndDate(localEndDate ?? "");
          setStartTime(localStartTime ?? "");
          setEndTime(localEndTime ?? "");
          // setTravellerCount(
          //   data.total_traveller_count !== undefined && data.total_traveller_count !== null
          //     ? data.total_traveller_count.toString()
          //     : ""
          // );

          const totalCountFromTemplates = (
            data.translated_templates || []
          ).reduce(
            (sum: number, item: { total_traveller_count?: number }) =>
              sum + (item.total_traveller_count ?? 0),
            0
          );

          setTravellerCount(totalCountFromTemplates.toString());

          setPremiumNumbers(Boolean(data.premium_number));
          setDuplicateMessages(Boolean(data.duplicate_msg));
        }
      } catch (err) {
        // Optionally handle API error UI
      }
      setIsLoading(false);
    })();
  }, [campaignId, operatorUrl, adminUrl, operatorId]);

  // --- For CampaignDetailsCard: country tabs by selected channel ---
  const countryTabs = useMemo(() => {
    const countryIsos = translatedTemplates
      .filter((tpl) => tpl.channel_id === activeChannel)
      .map((tpl) => tpl.country_iso?.toUpperCase());
    return countryIsos.filter(
      (iso: string, idx: number, arr: string[]) =>
        iso && arr.indexOf(iso) === idx
    );
  }, [translatedTemplates, activeChannel]);

  // --- For CampaignDetailsCard: configs (per channel+country) ---
  const configsForTab = useMemo(() => {
    return translatedTemplates.filter(
      (tpl) =>
        tpl.channel_id === activeChannel &&
        tpl.country_iso?.toUpperCase() === activeCountry
    );
  }, [translatedTemplates, activeChannel, activeCountry]);

  // --- Tab handlers for details card ---
  const handleChannelTab = (id: number) => {
    setActiveChannel(id);
    const nextCountry =
      translatedTemplates
        .find((t) => t.channel_id === id)
        ?.country_iso?.toUpperCase() || "";
    setActiveCountry(nextCountry);
    setActiveConfigId(null);
  };
  const handleCountryTab = (iso: string) => {
    setActiveCountry(iso);
    setActiveConfigId(null);
  };
  const handleConfigClick = (configId: number) => setActiveConfigId(configId);

  // --- Build operatorKeys and operatorTemplates for preview dropdown ---
  useEffect(() => {
    if (!translatedTemplates.length) return;

    // --- Dropdown keys ---
    const keys: OperatorKey[] = translatedTemplates.map((tpl: any) => {
      const channelType =
        tpl.translated_template?.channel_type ||
        (tpl.channel_id === 2 ? "SMS" : "WhatsApp");
      const country = tpl.country_iso?.toUpperCase() || "";
      let label = tpl.channel_config_name;
      if (
        channelType.toLowerCase() === "sms" &&
        tpl.translated_template?.short_code
      ) {
        label += `-${tpl.translated_template.short_code}`;
      }
      label += ` (${countryNameMap[country] || country})`;
      return {
        channel: channelType,
        country,
        operatorId: tpl.channel_config_id,
        label,
      };
    });
    setOperatorKeys(keys);
    setSelectedOperatorKey(keys[0] || null);

    // --- operatorTemplates: channelType > country > operatorId
    const templates: Record<string, Record<string, Record<string, any>>> = {};
    translatedTemplates.forEach((tpl: any) => {
      const channelType =
        tpl.translated_template?.channel_type ||
        (tpl.channel_id === 2 ? "SMS" : "WhatsApp");
      const country = tpl.country_iso?.toUpperCase() || "";
      const operatorId = tpl.channel_config_id;
      if (!templates[channelType]) templates[channelType] = {};
      if (!templates[channelType][country])
        templates[channelType][country] = {};
      templates[channelType][country][operatorId] = tpl.translated_template;
    });
    setOperatorTemplates(templates);
  }, [translatedTemplates, countryNameMap]);


    const handleCheckDeletedStatus = async () => {
      try {
        const response = await axios.get(`${adminUrl}/GetCampaignDeletedStatus`, {
          params: { campaignId },
        });
  
        const isDeleted = response.data?.isDeleted ?? false;
  
        setIsDeleted(isDeleted);
  
        return isDeleted;
      } catch (error) {
        console.error("Error checking deleted status:", error);
  
        setIsDeleted(false);
  
        return false;
      }
    };


  const handleRejectAndCommentNotification = async (
    campaignId: number,

  ) => {
    try {
      const payload = {
        workspaceId: operatorId || 0,
        campaignId: campaignId,
        campaignName: campaignName,
        statusMark: "Unread",
        notificationType: "OperatorRejectComment",
        notificationData: {
          Message: `${workspaceName} rejected and commented on the campaign – ${campaignName}`,
          Comment: rejectComment || "",
        },
        role: "Admin",
      };

      console.log("Combined Notification payload:", payload);

      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,
        payload
      );

      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Rejection and comment notification sent successfully.",
        });
        setTimeout(() => {
          setIsLoading(false);
          navigate("/operatorNavbar/campaignlist");
        }, 800);
      } else {
        throw new Error("Notification API failed.");
      }
    } catch (error) {
      console.error("Notification error:", error);
      toast.toast({
        title: "Notification Error",
        description: "Could not send rejection and comment notification.",
      });
    }
  };

  const handleReject = async () => {
    let afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Rejected", comment: rejectComment }; // Prepare afterData with updated data
    let status = "Success"; // Default status is success
    let errorMessage = ""; // Initialize error message
    setIsLoading(true);

    const payload1 = {
      accountId: accountId,
      campaignId: campaignId, // Replace with actual campaign ID
      operatorId: operatorId, // Replace with actual operator ID
      status: "rejected", // If this is a rejection, should it be "rejected"?
      approver: "Operator", // Replace with actual user if needed
      timestamp: new Date().toISOString(),
      rejectComment: rejectComment,
    };

    try {
      // First API call to update operator approval status
      const response = await axios.post(
        `${operatorUrl}/UpdateOperatorApprovalStatus`,
        payload1
      );

      if (response.data.status === "Success") {
        await triggerN8NWebhook(false);
        await handleRejectAndCommentNotification(campaignId);

        console.log("✅ Success:", response.data.message);

        const payload2 = {
          userAccountId: Number(accountId),
          campaignId: campaignId,
          advertiserWorkspaceId: workspaceId,
          action: "operator rejected",
          actionPage: "Operator Campaign Approval",
          status: "rejected",
          beforeData: null,
          afterData: null,
          ipAddress: null,
          comments: rejectComment,
        };

        // Second API call to log the rejection action
        // const response2 = await axios.post(
        //   `${adminUrl}/AdminRejectCampaign`,
        //   payload2
        // );

        // Show success toast and redirect
        setRejectComment("");
        afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Rejected", comment: rejectComment }; // Prepare afterData with updated data
        status = "Success"; // Default status is success
        errorMessage = "Operator Campaign Rejection Success."; // Initialize error message
        toast.toast({
          title: "Success",
          description: "Campaign Rejected Successfully.",
        });

  // ✅ Call InsertAuditHistory after 5 seconds (same delay as navigate)
setTimeout(async () => {
  await axios.put(`${apiUrlAdminAcc}/UpdateAuditHistory`, {
    OperatorId: workspaceId,
    CampaignId: campaignId,
    AdvertiserWorkspaceId: workspaceId,
    Action: 'Operator Rejected',
    ActionPage: 'Admin Campaign Approval',
    Comments: rejectComment
  });

  navigate("/operatorNavbar/campaignlist");
}, 3000); // ✅ Keep your original 2000ms delay

      } else {
        afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Rejected", comment: rejectComment }; // Prepare afterData with updated data
        status = "Failure"; // Default status is success
        errorMessage = "Operator Campaign Rejection Failed."; // Initialize error message
        toast.toast({
          title: "Error",
          description: response.data?.Status_Description || "Approval failed.",
        });
      }
    } catch (error: any) {
      afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Rejected", comment: rejectComment }; // Prepare afterData with updated data
      status = "Failure"; // Default status is success
      errorMessage = "Operator Campaign Rejection Failed."; // Initialize error message
      toast.toast({
        title: "Error",
        description:
          error?.response?.data?.Status_Description ||
          error.message ||
          "Unknown error.",
      });
    } finally {
      // Prepare the audit data (after the role update attempt)
      const auditData = {
        UserAccountId: accountId, // Ensure userAccountId is available
        CampaignId: campaignId, // If applicable, set the campaign ID
        AdvertiserWorkspaceId: workspaceId,
        Action: "Operator Campaign Approval",
        ActionPage: "Operator Campaign Approval Page",
        Status: status, // "Success", "Failure", or "Error"
        KeyName: null,
        KeyValue: 0,
        // Current role before update
        AfterData: JSON.stringify(afterData), // The updated role after change
        // Pass the IP address
        Comments: errorMessage, // Comment for audit log
        ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null
      };

      // Call the async method to insert audit data once at the end
      await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, auditData);
      setIsLoading(false);
    }
  };


  // const triggerN8NWebhook = async (isApproved: boolean) => {
  //   try {
  //     const payload = {
  //       operator_mapping_id: operatorMappingId, // ✅ match model
  //       campaignID: campaignId,
  //       campaignName: campaignName,
  //       advertiserID: advertiserAccountId?.toString(),
  //       workspaceID: workspaceId?.toString(), //Operator's
  //       wrokspaceName: "",
  //       request_type: isApproved?"WABA_approval":"reject_campaign", // ✅ match model
  //       comments: isApproved?"Approve this Campaign":rejectComment,
  //       advertiser_email: advertiserEmail, // optional,
  //       channel_id: [""],
  //       channel_type: [""],
  //     };

  //     const response = await axios.post(`${operatorUrl}/N8NOperatorCampaignApprovalandRejectionWebhook`, payload);

  //     if (response.status !== 200) {
  //       console.error("❌ Webhook Trigger Failed");
  //     } else {
  //       console.log("✅ Webhook Triggered Successfully");
  //     }
  //   } catch (error) {
  //     console.error("❗ Error in triggerN8NWebhook", error);
  //   }
  // };

  const handleApproveNotification = async (campaignId: number) => {
    try {
      const payload = {
        workspaceId: operatorId || 0,
        campaignId,
        campaignName,
        statusMark: "Unread",
        notificationType: "AdminCampaignApprove",
        notificationData: {
          Message: `${workspaceName} approved campaign- ${campaignName}`,
        },
        role: "Admin",
      };



      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,
        payload
      );

      if (response.data.status !== "Success") {
        throw new Error("Notification API failed.");
      }
    } catch (error) {

      toast.toast({
        title: "Notification Error",
        description: "Campaign approved but notification could not be sent.",
      });
    }
  };


  const triggerN8NWebhook = async (isApproved: boolean) => {
    try {
      const payload = {
        operator_mapping_id: operatorMappingId,
        campaignID: campaignId,
        campaignName: campaignName,
        advertiserID: advertiserAccountId?.toString(),
        workspaceID: workspaceId?.toString(),
        wrokspaceName: "",
        request_type: isApproved ? "operator_approval_campaign" : "operator_reject_campaign",
        comments: isApproved ? "Approve this Campaign" : rejectComment,
        advertiser_email: advertiserEmail,
        channel_id: [""],
        channel_type: [""],
      };

      const response = await axios.post(
        `${operatorUrl}/N8NOperatorCampaignApprovalandRejectionWebhook`,
        payload
      );

      if (response.status !== 200) {
        console.error("❌ Webhook Trigger Failed");
      } else {
        console.log("✅ Webhook Triggered Successfully");
      }
    } catch (error) {
      console.error("❗ Error in triggerN8NWebhook", error);
    }
  };
  const handleApprove = async () => {
    setIsLoading(true);

    let afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Approved" }; // Prepare afterData with updated data
    let status = "Success"; // Default status is success
    let errorMessage = ""; // Initialize error message

    const payload = {
      accountId: accountId,
      campaignId: campaignId, // replace with actual campaign ID
      operatorId: operatorId, // replace with actual operator ID
      status: "approved", // or "REJECTED"
      approver: "Operator", // replace with the logged-in user
      timestamp: new Date().toISOString(),
      rejectComment: "",
    };


    try {
      const response = await axios.post(
        `${operatorUrl}/UpdateOperatorApprovalStatus`, // Replace with your actual API endpoint
        payload
      );

      if (response.data.status === "Success") {
        //Triggering N8N 
        await triggerN8NWebhook(true);
        await handleApproveNotification(campaignId);
        console.log("✅ Success:", response.data.message);
        afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Approved" }; // Prepare afterData with updated data
        status = "Success"; // Default status is success
        errorMessage = "Operator Campaign Approval Success."; // Initialize error message
        toast.toast({
          title: "Success",
          description: "Campaign Approved Successfully.",
        });
        setTimeout(() => {
          navigate("/operatorNavbar/campaignlist");
        }, 2000);
      } else {
        afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Approved" }; // Prepare afterData with updated data
        status = "Failure"; // Default status is success
        errorMessage = "Operator Campaign Approval Failed."; // Initialize error message
        toast.toast({
          title: "Error",
          description: response.data.message || "Approval failed.",
        });
      }
    } catch (error: any) {
      afterData = { AccountId: accountId, PartnerId: workspaceId, status: "Approved" }; // Prepare afterData with updated data
      status = "Failure"; // Default status is success
      errorMessage = "Operator Campaign Approval Failed."; // Initialize error message
      toast.toast({
        title: "Error",
        description: error?.response?.data?.message || error.message || "Unknown error.",
      });
    } finally {
      // Prepare the audit data (after the role update attempt)
      const auditData = {
        UserAccountId: accountId, // Ensure userAccountId is available
        CampaignId: campaignId, // If applicable, set the campaign ID
        AdvertiserWorkspaceId: workspaceId,
        Action: "Operator Campaign Approval",
        ActionPage: "Operator Campaign Approval Page",
        Status: status, // "Success", "Failure", or "Error"
        KeyName: null,
        KeyValue: 0,
        // Current role before update
        AfterData: JSON.stringify(afterData), // The updated role after change
        // Pass the IP address
        Comments: errorMessage, // Comment for audit log
        ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null
      };

      // Call the async method to insert audit data once at the end
      await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, auditData);

      setIsLoading(false);
    }
  };

  // --- UI: two-column layout, exactly as in your screenshot ---
  return (
    <>
      {isLoading ? (
        <div className="loading-overlay">
          <CircularProgress color="primary" />
        </div>
      )  : isDeleted ? (
              <div className="flex flex-col items-center justify-center h-[500px] gap-4 text-center px-6">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a2 2 0 00-2-2H9a2 2 0 00-2 2m10 0H5"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-700">
                  Campaign Deleted
                </h2>
                <p className="text-gray-500 max-w-md">
                  This campaign was deleted and no more actions are required.
                </p>
                <Button
                  variant="outline"
                  className="w-[85px] border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
                  onClick={() => navigate("/adminNavbar/campaigns")}
                >
                  Back
                </Button>
              </div>
            ) : (
        <div>
          <RejectCampaignDialog
            open={showRejectDialog}
            comment={rejectComment}
            setComment={setRejectComment}
            onClose={() => setShowRejectDialog(false)}
            onConfirm={() => {
              setShowRejectDialog(false);
              console.log(rejectComment);
              handleReject();
            }}
          />
          <div className="overflow-y-auto ml-[-7px]">
            <Toaster />
            <div className="fixed flex justify-end gap-4 mr-[40px] items-end right-[0px] top-[-15px] z-20 ">
              {campaignStatus !== "already approved" &&
                campaignStatus !== "rejected" &&
                campaignStatus !== "waiting for admin approval" &&
                campaignApprovalStatus === false && (
                  <Button
                    variant={"outline"}
                    disabled={isLoading}
                    className="w-[85px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    onClick={() => setShowRejectDialog(true)}
                  >
                    {isLoading ? "Rejecting..." : "Reject"}
                  </Button>
                )}
              {campaignStatus !== "already approved" &&
                campaignStatus !== "rejected" &&
                campaignStatus !== "waiting for admin approval" &&
                !campaignApprovalStatus ? (
                <Button
                  className="w-[80px] text-[#F8FAFC]"
                  onClick={handleApprove}
                >
                  {isLoading ? "Approving..." : "Approve"}
                </Button>
              ) : (
                <Button
                  className="w-[85px] text-[#F8FAFC]"
                  onClick={() => {
                    navigate("/operatorNavbar/campaignlist");
                  }}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-6 overflow-y-auto p-4">
              <div className="flex-grow">
                <CampaignDetailsCard
                  campaignName={campaignName}
                  fromCountry={fromCountryName.join(", ")}
                  toCountry={toCountryName}
                  startDate={startDate}
                  endDate={endDate}
                  startTime={startTime}
                  endTime={endTime}
                  travellerCount={travellerCount}
                  premiumNumbers={premiumNumbers}
                  duplicateMessages={duplicateMessages}
                  channels={channels}
                  countryTabs={countryTabs}
                  countryNameMap={countryNameMap}
                  activeChannel={activeChannel}
                  activeCountry={activeCountry}
                  onSelectChannel={handleChannelTab}
                  onSelectCountry={handleCountryTab}
                  configsForTab={configsForTab}
                  activeConfigId={activeConfigId}
                  onSelectConfig={handleConfigClick}
                  translatedTemplates={translatedTemplates}
                />
              </div>
              {/* --- Preview (right) --- */}
              <OperatorMessagePreviewCard
                operatorKeys={operatorKeys}
                selectedOperatorKey={selectedOperatorKey}
                setSelectedOperatorKey={setSelectedOperatorKey}
                operatorTemplates={operatorTemplates}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OperatorCampaignApprovalPage;
